import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AuthGuardData, createAuthGuard } from 'keycloak-angular';
import { inject, isDevMode } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppService } from '../services/app.service';
import { IxNgValidationService, IxValidationRecord } from 'ix-ng-forms';
import { IxNgComValidationService } from 'ix-ng-components';
import { NavigationEnd } from '@angular/router';
import { filter, take } from 'rxjs';

const isAccessAllowed = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authData: AuthGuardData,
): Promise<boolean | UrlTree> => {
  const appService = inject(AppService);
  const router = inject(Router);
  const ixNgValidationService = inject(IxNgValidationService);
  const ixNgComValidationService = inject(IxNgComValidationService);

  const { authenticated, grantedRoles } = authData;

  if (!authenticated) {
    return router.parseUrl('/vstopnaStran');
  }

  if (!grantedRoles?.resourceRoles || Object.values(grantedRoles.resourceRoles).length === 0) {
    return router.parseUrl('/access-denied');
  }

  // Uporabniška vloga, ki je potrebna za dostop do aplikacije
  const requiredUserRole: string | undefined = route.data['role'];

  const hasRequiredRole = (role: string | undefined): boolean => {
    if (!role) {
      if (isDevMode()) {
        console.error(`authGuard: pot '${state.url}' nima nastavljene 'role' v route.data`);
      }
      return false;
    }
    return Object.values(grantedRoles.resourceRoles).some((roles) => roles.includes(role));
  };

  const objectCode = route.data['objectCode'];
  let hasAccess;

  if (objectCode) {
    hasAccess = false;
    const action = route.data['action'];

    if (Array.isArray(action)) {
      for (const item of action) {
        const result = await firstValueFrom(appService.hasAccess(objectCode, item));
        if (result) {
          hasAccess = true;
          break; // Stop checking other items immediately
        }
      }
    } else {
      // Fallback for single string or other types
      hasAccess = await firstValueFrom(appService.hasAccess(objectCode, action));
    }
  } else {
    hasAccess = true;
  }

  if (hasRequiredRole(requiredUserRole) && hasAccess) {
    return true;
  } else {
    // if we entered the page through direct link
    // we redirect to zacetnaStran
    // if user tried to go to a page without premision we dont allow the url change
    const isDirectLink = !router.navigated;
    if (isDirectLink) {
      router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          take(1),
        )
        .subscribe(() => {
          const record = new IxValidationRecord();
          record.errorMessage = 'Nimate dostopa';
          record.code = 'Dostop';
          ixNgValidationService.validationRecordList.push(record);
          ixNgComValidationService.appComValidationOpen = true;
        });
      return router.parseUrl('/zacetnaStran');
    }
    // Display Access denied error
    const record = new IxValidationRecord();
    record.errorMessage = 'Nimate dostopa';
    record.code = 'Dostop';
    ixNgValidationService.validationRecordList.push(record);
    ixNgComValidationService.appComValidationOpen = true;
    return false;
  }
};

export const authGuard = createAuthGuard<CanActivateFn>(isAccessAllowed);
